import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, FileDown } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { downloadPdf, apiFail } from "../lib/p2";
import { NoAccess } from "../components/common";

const REPORTS = [{ slug: "revised-scope", title: "reports.scope_title", date: "reports.overall_date", file: "Valeriia-Revised-Scope" }, { slug: "overall-status", title: "reports.overall_title", date: "reports.overall_date", file: "Valeriia-Overall-Status-Report" }, { slug: "phase1-status", title: "reports.phase1_title", file: "Valeriia-Phase1-Status-Report" }];
const btn = "inline-flex min-h-[52px] items-center gap-2 rounded-full px-6 font-semibold transition-transform hover:-translate-y-0.5";

function ReportCard({ r, first }) {
  const { t } = useTranslation();
  const [meta, setMeta] = useState(null);
  useEffect(() => { api.get(`/reports/${r.slug}`).then((x) => setMeta(x.data)).catch(() => setMeta({})); }, [r.slug]);
  const dl = (ext) => downloadPdf(`/reports/${r.slug}.${ext}`, `${r.file}.${ext}`).catch(apiFail(t));
  return (
    <section className="rounded-3xl border bg-white/80 p-6 shadow-sm" data-testid={`report-${r.slug}`}>
      {first ? <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid={`report-title-${r.slug}`}>{t(r.title)}</h1>
        : <h2 className="font-display text-2xl font-extrabold tracking-tight" data-testid={`report-title-${r.slug}`}>{t(r.title)}</h2>}
      <p className="mt-2 text-muted-foreground" data-testid={`report-date-${r.slug}`}>{r.date ? t(r.date) : meta?.date}</p>
      <div className="mt-5 flex flex-wrap gap-3">
        <button type="button" onClick={() => dl("docx")} data-testid={`download-docx-${r.slug}`} className={`${btn} bg-ink text-white`}><FileText className="h-5 w-5" aria-hidden />{t("reports.download_docx")}</button>
        <button type="button" onClick={() => dl("pdf")} data-testid={`download-pdf-${r.slug}`} className={`${btn} border bg-white`}><FileDown className="h-5 w-5" aria-hidden />{t("reports.download_pdf")}</button>
      </div>
    </section>
  );
}

const HANDOVER_LABELS = { "Valeriia_Handover_Pack.zip": "reports.handover_pack", "Valeriia_Handover_Checklist_and_Templates.xlsx": "reports.handover_xlsx", "Valeriia_Handover_Guide.docx": "reports.handover_guide", "Valeriia_Handover_Longform_Templates.docx": "reports.handover_longform", "Valeriia_Handover_Backup_CSV_MD.zip": "reports.handover_backup" };
const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function HandoverCard() {
  const { t } = useTranslation();
  const [files, setFiles] = useState(null);
  useEffect(() => { api.get("/reports/handover/files").then((x) => setFiles(x.data)).catch(() => setFiles([])); }, []);
  const dl = (name) => downloadPdf(`/reports/handover/files/${encodeURIComponent(name)}`, name).catch(apiFail(t));
  return (
    <section className="rounded-3xl border bg-white/80 p-6 shadow-sm" data-testid="report-handover">
      <h2 className="font-display text-2xl font-extrabold tracking-tight" data-testid="report-title-handover">{t("reports.handover_title")}</h2>
      <p className="mt-2 text-muted-foreground">{t("reports.handover_sub")}</p>
      <ul className="mt-5 space-y-3">
        {(files || []).map((f) => (
          <li key={f.name} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-card p-4" data-testid={`handover-item-${f.name}`}>
            <div className="min-w-0"><p className="font-semibold">{t(HANDOVER_LABELS[f.name])}</p><p className="break-all text-sm text-muted-foreground">{f.name} · {kb(f.size)}</p></div>
            <button type="button" onClick={() => dl(f.name)} data-testid={`download-handover-${f.name}`} className={`${btn} border bg-white`}><FileDown className="h-5 w-5" aria-hidden />{t("reports.download")}</button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function Reports() {
  const { t } = useTranslation();
  const { me } = useAuth();
  if (!me?.user?.platform_admin) return <NoAccess />;
  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="reports-page">
      <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground" data-testid="reports-heading">{t("reports.heading")}</p>
      {REPORTS.map((r, i) => <ReportCard key={r.slug} r={r} first={i === 0} />)}
      <HandoverCard />
    </div>
  );
}
